import { StyledRecordDeletionDialog } from "../../../../utils/styled";
import { BUTTON_VARIANTS } from "@cdkglobal/radial";
import { useUIStateContext } from "../../../../contexts/UIStateContext";
import { useUserContext } from "../../../../contexts/UserContext";
import {
  DeleteRecordRequest,
  RowDataReducerState,
  RowDataReducerAction,
  ToastInfo,
  JsonSchema,
} from "../../../../types";
import { useDeleteRecords, useRefetchRowDataList } from "../../../../hooks";
import { useRef, useEffect } from "react";
import { TOAST_MESSAGES } from "../../constants";
import { ampli } from "../../../../ampli";
import {
  getSchemaTitles,
  getFieldsWithUITitles,
  getUpdatedSetupBaseRequestInfo,
  getDeleteConfirmationMessage,
  getUpdatedDeleteRowData,
} from "../../helpers";
import { useIntl } from "react-intl";

type RecordDeletionDialogProps = {
  rowDataReducerState: RowDataReducerState;
  dispatch: React.Dispatch<RowDataReducerAction>;
  schema: JsonSchema;
};

export default function RecordDeletionDialog({
  rowDataReducerState,
  dispatch,
  schema,
}: Readonly<RecordDeletionDialogProps>): JSX.Element {
  const intl = useIntl();
  const {
    selectedDashboardTabSectionKey,
    selectedTabSectionKey,
    isRecordDeleteDialogOpen,
    setIsRecordDeleteDialogOpen,
    setLandingPageToast,
    setCurrentPage,
    setSearchValue,
  } = useUIStateContext();
  const { setupBaseRequestInfo, setTableTitleInfo, selected } =
    useUserContext();

  const { rowData, resetState } = rowDataReducerState;

  const mountedRef = useRef(true);

  const { confirmationMessage, additionalMessage } =
    getDeleteConfirmationMessage({
      setup: schema.title,
      schema,
      rowData,
      intl,
    });

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleDialogClose = () => {
    setIsRecordDeleteDialogOpen(false);
    dispatch({
      type: "UPDATE_ROW_DATA_REDUCER_STATE",
      payload: {
        rowData: resetState?.newEntryRowData ?? {},
      },
    });
  };

  const onSuccessCallback = (response: ToastInfo) => {
    setLandingPageToast(response);
    if (response.variant === "positive") {
      refetchRowDataList();
    }
  };

  const { refetch: refetchRowDataList } = useRefetchRowDataList({
    setupBaseRequestInfo,
    headerLabel: undefined,
    dispatch,
    setTableTitleInfo,
  });

  const { mutate: deleteRecord } = useDeleteRecords({
    onSuccessCallback,
  });

  const handleRecordDeletion = () => {
    handleDialogClose();
    setLandingPageToast(TOAST_MESSAGES.DELETING());

    const schemaTitles = getSchemaTitles(schema);
    const fieldsWithTitles = getFieldsWithUITitles(rowData, schemaTitles);

    ampli.deleteRecord({
      "Delete Record": true,
      Action: "Delete Entry",
      "Action Triggered": true,
      Store: selected.store.value,
      Application: selected.department.value,
      Setup: selected.setup.value,
      ...fieldsWithTitles,
    });
    const updatedSetupBaseRequestInfo = getUpdatedSetupBaseRequestInfo(
      setupBaseRequestInfo,
      selectedTabSectionKey,
      selectedDashboardTabSectionKey,
    );
    const updatedRowData = getUpdatedDeleteRowData(rowData, schema.title);
    const request: DeleteRecordRequest = {
      ...updatedSetupBaseRequestInfo,
      record: updatedRowData,
    };
    deleteRecord(request);
    setCurrentPage(1);
    setSearchValue("");
  };

  return (
    <StyledRecordDeletionDialog
      id="delete-record-dialog"
      dataTestId="delete-record-confirm-dialog"
      buttonsProps={[
        {
          id: "delete-record-dialog-cancel",
          onClick: handleDialogClose,
          text: intl.formatMessage({
            id: "dialog.cancel",
            defaultMessage: "Cancel",
          }),
          variant: BUTTON_VARIANTS.TEXT,
        },
        {
          id: "delete-record-dialog-delete",
          onClick: handleRecordDeletion,
          text: intl.formatMessage({
            id: "dialog.delete",
            defaultMessage: "Delete",
          }),
          variant: BUTTON_VARIANTS.PRIMARY,
        },
      ]}
      isOpen={isRecordDeleteDialogOpen}
      onClose={handleDialogClose}
      title={intl.formatMessage({
        id: "dialog.title",
        defaultMessage: "Delete Record?",
      })}
    >
      <p>{confirmationMessage}</p>
      <p>{additionalMessage}</p>
    </StyledRecordDeletionDialog>
  );
}
